// @flow strict
import _get from 'lodash.get'
import * as React from 'react'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import ReactHtmlParser from 'react-html-parser'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import objectToQueryString from '@mondough/public-web-components/src/utils/objectToQueryString'

import SocialEmbeds from '../components/SocialEmbeds/SocialEmbeds'
import CallToAction from '../components/CallToAction/CallToAction'
import { ResponsiveImage } from '@mondough/public-web-components'
import {
  buildDefaultImage,
  buildImageSourceSets,
} from '@mondough/public-web-components/src/utils/buildPostResponsiveImages'

// TYPES
type metaData = {
  slug: string,
}

// COMPONENTS
const HTMLSnippet = ({ children }: { children: string }) => (
  <>{ReactHtmlParser(children)}</>
)

// HELPERS
const flattenContentfulFieldsObject = (fieldsObject, keyToFlatten) =>
  Object.keys(fieldsObject).reduce((accumulator, item) => {
    accumulator[item] = fieldsObject[item][keyToFlatten]
    return accumulator
  }, {})

const addAdjustParamsIfNeeded = (link, slug) => {
  const isAdjustLink = link.indexOf('https://app.adjust.com') === 0
  if (isAdjustLink) {
    const hasQueryParams = link.indexOf('?') < 0
    const queryString = objectToQueryString(
      { monzo_referrer: slug },
      {
        startWithQuestionMark: hasQueryParams,
        encoded: true,
      },
    )
    return `${link}${queryString}`
  }
  return link
}

const renderText = text =>
  text
    .split('\n')
    .reduce(
      (children, textSegment, index) => [
        ...children,
        index > 0 && <br key={index} />,
        textSegment,
      ],
      [],
    )
// find embedded html snippets, custom block level components and render them
const renderEmbeddedEntryBlock = (node: mixed, slug: string) => {
  const nodeType = _get(node, 'data.target.sys.contentType.sys.id')
  const fields = _get(node, `data.target.fields`)

  switch (nodeType) {
    case 'htmlSnippet':
      const content = _get(node, `data.target.fields.content['en-GB']`)
      return <HTMLSnippet>{content}</HTMLSnippet>
    case 'webBlockCta':
      const {
        text,
        cta,
        category,
        action,
        label,
        href,
      } = flattenContentfulFieldsObject(fields, 'en-GB')

      return (
        <CallToAction
          text={text}
          cta={cta}
          category={category}
          action={action}
          label={label}
          href={addAdjustParamsIfNeeded(href, slug)}
        />
      )
    case 'mediaEmbed':
      const { urlOrId, type } = flattenContentfulFieldsObject(fields, 'en-GB')
      return <SocialEmbeds type={type} id={urlOrId} />
    default:
      return null
  }
}

// find embedded images and render them
export const renderBlockEmbeddedAsset = (node: mixed) => {
  const src = _get(node, `data.target.fields.file['en-GB'].url`)
  const assetTitle = _get(node, `data.target.fields.title['en-GB']`, '')
  const assetDescription = _get(
    node,
    `data.target.fields.description['en-GB']`,
    '',
  )
  const alt = assetDescription || assetTitle
  const desktopMaxImageWidth = 640
  const imageWidths = [288, 328, 343, 361, 382, 735, 674]
  const sources = {
    srcSet: buildImageSourceSets(imageWidths, src),
    sizes: `(max-width: 639px) 90vw, ${desktopMaxImageWidth}px`,
  }
  const defaultImage = buildDefaultImage(src, desktopMaxImageWidth * 2)

  return (
    <ResponsiveImage
      alt={alt}
      defaultImage={defaultImage}
      sources={[sources]}
    />
  )
}

// find inlined html snippets and render them
const renderInlineEmbeddedEntry = (node: mixed) => {
  const nodeType = _get(node, 'data.target.sys.contentType.sys.id')
  if (nodeType === 'htmlSnippet') {
    const nodeContent = _get(node, `data.target.fields.content['en-GB']`)
    return <HTMLSnippet>{nodeContent}</HTMLSnippet>
  }
}

const createRendererOptions = ({ slug }: metaData) => ({
  renderText,
  renderNode: {
    [BLOCKS.EMBEDDED_ENTRY]: node => renderEmbeddedEntryBlock(node, slug),
    [BLOCKS.EMBEDDED_ASSET]: renderBlockEmbeddedAsset,
    [INLINES.EMBEDDED_ENTRY]: renderInlineEmbeddedEntry,
  },
})

export default (data: metaData, content: string) =>
  documentToReactComponents(content, createRendererOptions(data))

export { renderEmbeddedEntryBlock }
